import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { cn } from "../lib/cn";
import { useI18n } from "../lib/i18n";

/**
 * Dialog — Radix wrapper, shadcn-style.
 * Forward-refs preserved on every leaf so Radix portal slotting stays
 * transparent. Uses Ando ink-1 / cream surfaces and the global animation
 * tokens (fade-in / drop-and-bounce) keyed off Radix's data-state attrs.
 */

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-ink-1/50 backdrop-blur-[2px] data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * a11y fallback title. Radix Dialog primitive **requires** a Title
   * inside Content; consumers that don't render a visible
   * ``<DialogTitle>`` MUST rely on this so we can mount a sr-only one —
   * otherwise Radix logs ``DialogTitle must be used within Dialog`` on
   * every open. Default ``"Finestra"``.
   */
  ariaTitle?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, ariaTitle, ...props }, ref) => {
  const { i18n } = useI18n();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 grid w-full max-w-lg gap-4 rounded-lg border border-hairline bg-card p-6 text-card-foreground shadow-lg duration-200",
          "data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
          className
        )}
        {...props}
      >
        {/* Always-on sr-only Title — Radix Dialog logs
            ``DialogTitle must be used within Dialog`` when no Title is
            mounted at any time, INCLUDING during the close-animation
            window when consumers conditionally render their visible
            ``<DialogTitle>`` (see ``AutomationDetailDrawer``: body is
            gated on ``automation !== null`` which flips before the
            150ms exit animation finishes). Radix happily accepts
            multiple Title nodes and uses the first as the
            ``aria-labelledby`` target, so a visible ``<DialogTitle>`` in
            children continues to win. */}
        <VisuallyHidden.Root>
          <DialogPrimitive.Title>
            {ariaTitle ?? i18n._(msg`Finestra`)}
          </DialogPrimitive.Title>
        </VisuallyHidden.Root>
        {children}
        <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">{i18n._(msg`Chiudi`)}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-1.5 text-center sm:text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-display font-semibold text-ink-1 text-lg leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-ink-2 text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
